/**
 * 家庭数据共享Context
 * 用于缓存children、rules等常用数据，避免重复API调用
 */
import { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from 'react';
import { useAuth } from '../api/auth';
import { api } from '../api';
import type { ChildInfo, RewardRuleInfo, PunishRuleInfo } from '../api/types';

interface FamilyDataContextType {
  // 数据
  children: ChildInfo[];
  rewardRules: RewardRuleInfo[];
  punishRules: PunishRuleInfo[];
  childrenMap: Record<string, { name: string; nickname?: string }>;

  // 状态
  loading: boolean;
  lastFetchTime: number;

  // 方法
  refreshChildren: () => Promise<void>;
  refreshRules: () => Promise<void>;
  refreshAll: () => Promise<void>;
  forceRefresh: () => Promise<void>;
  ensureRulesLoaded: () => Promise<void>;
  getChildById: (childId: string) => ChildInfo | undefined;
}

const FamilyDataContext = createContext<FamilyDataContextType | undefined>(undefined);

// 缓存时间：30分钟
const CACHE_DURATION = 30 * 60 * 1000;

interface CacheData {
  timestamp: number;
  familyId: string;
  children: ChildInfo[];
  rewardRules: RewardRuleInfo[];
  punishRules: PunishRuleInfo[];
}

function getCachedData(familyId: string): CacheData | null {
  try {
    const cached = localStorage.getItem('family_data_cache');
    if (!cached) return null;
    const data: CacheData = JSON.parse(cached);
    if (data.familyId !== familyId) return null;
    if (Date.now() - data.timestamp > CACHE_DURATION) return null;
    return data;
  } catch {
    return null;
  }
}

function setCachedData(familyId: string, children: ChildInfo[], rewardRules: RewardRuleInfo[], punishRules: PunishRuleInfo[]) {
  try {
    const data: CacheData = {
      timestamp: Date.now(),
      familyId,
      children,
      rewardRules,
      punishRules
    };
    localStorage.setItem('family_data_cache', JSON.stringify(data));
  } catch {
    // 忽略缓存写入错误
  }
}

export function FamilyDataProvider({ children: propChildren }: { children: ReactNode }) {
  const { currentFamily } = useAuth();

  const [children, setChildren] = useState<ChildInfo[]>([]);
  const [rewardRules, setRewardRules] = useState<RewardRuleInfo[]>([]);
  const [punishRules, setPunishRules] = useState<PunishRuleInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastFetchTime, setLastFetchTime] = useState(0);
  const [rulesLoaded, setRulesLoaded] = useState(false);

  // 构建childrenMap（使用useMemo保持引用稳定）
  const childrenMap: Record<string, { name: string; nickname?: string }> = useMemo(() => {
    const map: Record<string, { name: string; nickname?: string }> = {};
    children.forEach(child => {
      map[child.id] = { name: child.name, nickname: child.nickname };
    });
    return map;
  }, [children]);

  // 获取children
  const refreshChildren = useCallback(async () => {
    if (!currentFamily?.id) return;
    try {
      const res = await api.family.getChildren(currentFamily.id);
      if (res.code === 0 && res.data) {
        setChildren(res.data);
      }
    } catch (error) {
      console.error('Failed to fetch children:', error);
    }
  }, [currentFamily?.id]);

  // 获取rules
  const refreshRules = useCallback(async () => {
    if (!currentFamily?.id) return;
    try {
      const [rewardRes, punishRes] = await Promise.all([
        api.rules.getRewardRules(currentFamily.id),
        api.rules.getPunishRules(currentFamily.id)
      ]);
      if (rewardRes.code === 0) setRewardRules(rewardRes.data || []);
      if (punishRes.code === 0) setPunishRules(punishRes.data || []);
    } catch (error) {
      console.error('Failed to fetch rules:', error);
    }
  }, [currentFamily?.id]);

  // 获取所有数据（仅加载children，rules延迟加载）
  const refreshAll = useCallback(async () => {
    if (!currentFamily?.id) return;

    // 切换家庭时重置规则加载状态，确保新家庭的规则会被加载
    setRulesLoaded(false);

    // 尝试从缓存加载
    const cached = getCachedData(currentFamily.id);
    if (cached) {
      setChildren(cached.children);
      setRewardRules(cached.rewardRules);
      setPunishRules(cached.punishRules);
      setRulesLoaded(true);
      setLoading(false);
      setLastFetchTime(cached.timestamp);

      // 后台刷新children
      try {
        const childrenRes = await api.family.getChildren(currentFamily.id);
        if (childrenRes.code === 0) {
          setChildren(childrenRes.data || []);
        }
      } catch (error) {
        console.error('Failed to refresh children:', error);
      }
      return;
    }

    // 无缓存，仅加载children
    setLoading(true);
    try {
      const childrenRes = await api.family.getChildren(currentFamily.id);
      setChildren(childrenRes.code === 0 ? childrenRes.data || [] : []);
      setLastFetchTime(Date.now());
    } catch (error) {
      console.error('Failed to fetch children:', error);
    } finally {
      setLoading(false);
    }
  }, [currentFamily?.id]);

  // 根据ID获取child
  const getChildById = useCallback((childId: string) => {
    return children.find(c => c.id === childId);
  }, [children]);

  // 强制刷新（清除缓存）
  const forceRefresh = useCallback(async () => {
    if (!currentFamily?.id) return;
    localStorage.removeItem('family_data_cache');
    setLoading(true);
    setRulesLoaded(false);
    try {
      const childrenRes = await api.family.getChildren(currentFamily.id);
      setChildren(childrenRes.code === 0 ? childrenRes.data || [] : []);
      setLastFetchTime(Date.now());
    } catch (error) {
      console.error('Failed to force refresh data:', error);
    } finally {
      setLoading(false);
    }
  }, [currentFamily?.id]);

  // 确保规则已加载（延迟加载）
  const ensureRulesLoaded = useCallback(async () => {
    if (rulesLoaded || !currentFamily?.id) return;
    try {
      const [rewardRes, punishRes] = await Promise.all([
        api.rules.getRewardRules(currentFamily.id),
        api.rules.getPunishRules(currentFamily.id)
      ]);
      if (rewardRes.code === 0) setRewardRules(rewardRes.data || []);
      if (punishRes.code === 0) setPunishRules(punishRes.data || []);
      setRulesLoaded(true);
    } catch (error) {
      console.error('Failed to load rules:', error);
    }
  }, [currentFamily?.id, rulesLoaded]);

  // 初始化加载
  useEffect(() => {
    refreshAll();
  }, [refreshAll]);

  return (
    <FamilyDataContext.Provider value={{
      children,
      rewardRules,
      punishRules,
      childrenMap,
      loading,
      lastFetchTime,
      refreshChildren,
      refreshRules,
      refreshAll,
      forceRefresh,
      ensureRulesLoaded,
      getChildById
    }}>
      {propChildren}
    </FamilyDataContext.Provider>
  );
}

export function useFamilyData() {
  const context = useContext(FamilyDataContext);
  if (context === undefined) {
    throw new Error('useFamilyData must be used within a FamilyDataProvider');
  }
  return context;
}
